import React, { useEffect } from "react";
import { actionCreators } from "./redux/remainingSessionsSagas";
import { useDispatch, useSelector } from "react-redux";

import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import SectionToggle from "../../../components/sectionToggle/sectionToggle";
import PieChart from "../../../components/pieChart/pieChart";
import { DataPoints } from "./smartComponents/DataPoints";
import MembershipTypeFilter from "./smartComponents/MembershipTypeFilter";
import RemainingSessionsFilter from "./smartComponents/RemainingSessionsFilter";
import ReactDatePicker from "../../../components/reactDatePicker/reactDatePicker";
import RemainingSessionTable from "./smartComponents/RemainingSessionsTable";
import ViewFilter from "./smartComponents/ViewFilter";
import ProspectTypeFilter from "./smartComponents/ProspectTypeFilter";
import LearnMore from "../../../components/learnMore/learnMore";
import CategoriesFilter from "./smartComponents/CategoriesFilter";
import { UpdateButtons } from "./smartComponents/UpdateButtons";
import GroupBy from "./smartComponents/GroupBy";
import "./remainingSessions.scss";

const RemainingSessionsModule = (props) => {
  const dispatch = useDispatch();
  const {
    isLoading,
    updatesLoading,
    filters: { membership_types = [], prospect_types = [] },
    chartValues,
    currentUser: { useSecondaryCategories }
  } = useSelector((state) => state.sessions);
  const selectedView = useSelector((state) => state.sessions.selectedFilters.view);

  const disableUpdateButton =
    (selectedView === "Membership Type" && !membership_types.length) ||
    (selectedView === "Prospect Type" && !prospect_types.length);

  useEffect(() => {
    dispatch(actionCreators.fetchCurrentUser(props));
  }, []);

  const content = () => {
    return (
      <div>
        <div id="override-edit-selection-bar" className="edit-selections-bar">
          <div className="top-filters">
            <div className="left-side">
              {membership_types.length || prospect_types.length ? (
                <>
                  <div className="select-box" style={{ width: "160px" }}>
                    <p className="offers-title">View</p>
                    <ViewFilter />
                  </div>
                  {selectedView === "Category" ? (
                    <>
                      {useSecondaryCategories && (
                        <CategoriesFilter filter="primary_categories" name="Primary Category" />
                      )}
                      <CategoriesFilter
                        filter="secondary_categories"
                        name={`${useSecondaryCategories ? "Secondary" : ""} Category`}
                      />
                      <CategoriesFilter filter="sales_packages" name="Sales Package" />
                      <div className="select-box" style={{ width: "180px" }}>
                        <p className="offers-title">Group By</p>
                        <GroupBy />
                      </div>
                    </>
                  ) : selectedView === "Membership Type" ? (
                    <div className="select-box" style={{ width: "160px" }}>
                      <p className="offers-title">Membership Types</p>
                      <MembershipTypeFilter />
                    </div>
                  ) : (
                    <div className="select-box" style={{ width: "160px" }}>
                      <p className="offers-title">Prospect Types</p>
                      <ProspectTypeFilter />
                    </div>
                  )}

                  <div className="select-box" style={{ width: "160px" }}>
                    <p className="offers-title">Remaining Session</p>
                    <RemainingSessionsFilter />
                  </div>
                </>
              ) : null}
            </div>

            <div className="right-side">
              <ReactDatePicker
                calendarEndDate={props.calendarEndDate}
                disabled
                startDate={props.calendarEndDate}
              />
            </div>
          </div>
          <UpdateButtons params={props} disableUpdateButton={disableUpdateButton} />
        </div>
        {updatesLoading ? (
          <LoadingAnimation text="Updating data..." />
        ) : chartValues.length ? (
          <>
            <div className="pie-charts-wrapper">
              <DataPoints />
              {chartValues.map((values, idx) => (
                <div key={idx} className="pie-chart">
                  <PieChart
                    width={530}
                    height={300}
                    data={values}
                    legendPosition="right"
                    legendTooltipPosition="left"
                    showLegend
                    isLegendTooltip
                  />
                </div>
              ))}
            </div>
            <RemainingSessionTable />
          </>
        ) : (
          <p className="error">No data available</p>
        )}
      </div>
    );
  };

  return (
    <div id="remainingSessionModule">
      <SectionToggle
        title="Remaining Sessions"
        isLoading={isLoading}
        content={content()}
        autoHeight
        learnMore={<LearnMore title="Remaining Sessions" content={learnMoreContent} />}
      />
    </div>
  );
};

const learnMoreContent = (
  <>
    <p>
      The <b>Remaining Sessions</b> module was created to help a user easily see how many sessions
      are remaining for clients and groups of clients. There is not calendar control as we are
      showing the most current data we have per ClubReady.
    </p>
    <p>
      To verify the data, you will look into each client’s Bookings & Session Credit Status page and
      the ‘Sessions Credits’ tab is where we will be gathering the data.
    </p>
    <p>
      <i>
        Note – If a client has 26 or greater sessions remaining, they will NOT be shown in this
        module.
      </i>
    </p>
    <p>
      <b>View</b> <br /> This changes which set of clients we will group together. A user will have
      the option of selecting <b>Membership Type</b> or <b>Prospect Type.</b>
    </p>
    <p>
      When <b>Membership Type</b> (default) is selecting in the View, all available Membership Type
      will be available to selected under a second filter dropdown named Membership Types. If a user
      selects the <b>Prospect Type view</b>, the second filter will now be named Prospect Type show
      all Prospect Types available.
    </p>
    <p>
      <b>Remaining Sessions:</b> This filter will allow the user to filter down the clients by the
      number of remaining sessions they have available. There are three groupings that we use. The
      default is ‘1-2’ sessions remaining, but a user can also select ‘3-5’ and/or ‘6+’ or any
      combination of the three.
    </p>
    <br />
    <p>
      <b>Metrics and Charts</b>
      <br />
      <b>Remaining:</b> Will add up all the sessions remaining for the clients based on the above
      filters. <br />
      <b>Clients:</b> The number of clients based on the filters. <br />
      <b>Average:</b> The average number of sessions remaining based on the total remaining session
      and the number of clients.
    </p>
    <br />
    <p>
      The first pie chart will show a breakdown of the <b>Prospect Type</b> or{" "}
      <b>Membership Type</b> depending on the View filter. The second filter will show the groupings
      of remaining sessions.
    </p>
    <p>
      <b>Table:</b>
      <br />
      Each line will show a single client based on the above filters. <br />
      <b>Client ID:</b> Based on the info from ClubReady. <br />
      <b>Client Name:</b> Clients first and last name. <br />
      <b>Prospect Type:</b> Prospect Type at the time of the last update. <br />
      <b>Remaining:</b> This will show the number of sessions remaining based on the Bookings &
      Sessions Credit Status page.
      <br />
      <b>Expiration Date:</b> The closest expiration date will be shown. If there is no date, we
      will show ‘Can be used anytime - No expiration’ instead of a date.
    </p>
    <p>
      <b>Next Booking Date:</b> If the client has a booking in the next 14 days, the date of their
      scheduled booking will be displayed. If there is no date, there is not future booking.
    </p>
  </>
);

export default RemainingSessionsModule;
